import React, { useState, useEffect, useMemo } from 'react';
import { 
  FaMoneyBillWave, FaCreditCard, FaFutbol, FaGraduationCap, 
  FaFilter, FaCheckCircle, FaTimesCircle, FaChartPie, FaCalendarAlt,
  FaArrowLeft, FaDownload, FaSyncAlt, FaChevronLeft, FaChevronRight,
  FaSearch, FaTimes, FaTable
} from 'react-icons/fa';
import { 
  Chart as ChartJS, 
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale, 
  LinearScale, 
  BarElement, 
  Title 
} from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';
import { paymentDashboardService } from '../../../services/paymentDashboardService';
import { regionsService, facilitiesService } from '../../../services/sportsManagementService';
import './PaymentDashboard.scss';

// Register ChartJS components
ChartJS.register(
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale, 
  LinearScale, 
  BarElement, 
  Title
);

const PaymentDashboard = () => {
  const formatDateInputValue = (date) => {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getTodayString = () => formatDateInputValue(new Date());

  const getMonthStartString = () => {
    const today = new Date();
    return formatDateInputValue(new Date(today.getFullYear(), today.getMonth(), 1));
  };

  const [stats, setStats] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal State
  const [modalData, setModalData] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Pagination & Filtering
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    startDate: getMonthStartString(),
    endDate: getTodayString(),
    type: '',
    status: '',
    regionId: '',
    facilityId: ''
  });

  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [exportFilters, setExportFilters] = useState({
    startDate: getMonthStartString(),
    endDate: getTodayString(),
    type: '',
    status: 'success',
    regionId: '',
    facilityId: ''
  });
  const [isGeneralRevenueModalOpen, setIsGeneralRevenueModalOpen] = useState(false);
  const [generalRevenueFilters, setGeneralRevenueFilters] = useState({
    startDate: getMonthStartString(),
    endDate: getTodayString()
  });
  const [generalRevenueRows, setGeneralRevenueRows] = useState([]);
  const [generalRevenueLoading, setGeneralRevenueLoading] = useState(false);

  const setExportPreset = (preset) => {
    const today = new Date();
    let start = new Date(today);
    let end = new Date(today);

    switch (preset) {
      case 'today':
        break;
      case 'yesterday':
        start.setDate(today.getDate() - 1);
        end.setDate(today.getDate() - 1);
        break;
      case 'week':
        start.setDate(today.getDate() - today.getDay() + (today.getDay() === 0 ? -6 : 1)); // Monday
        break;
      case 'month':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        break;
      case 'lastMonth':
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        end = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      default:
        break;
    }

    setExportFilters(prev => ({
      ...prev,
      startDate: formatDateInputValue(start),
      endDate: formatDateInputValue(end)
    }));
  };

  const [regions, setRegions] = useState([]);
  const [facilities, setFacilities] = useState([]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const apiFilters = { ...filters };

      // Pass the entire filters object to get filtered stats
      const statsRes = await paymentDashboardService.getPaymentDashboardStats(apiFilters);
      if (statsRes?.isSuccess) {
        setStats(statsRes.data);
      }

      const txRes = await paymentDashboardService.getPaymentTransactionsPaged(page, 15, apiFilters);
      if (txRes?.isSuccess) {
        setTransactions(txRes.data.items || []);
        setTotalPages(Math.ceil((txRes.data.totalCount || 0) / 15));
      }
    } catch (error) {
      console.error('Dashboard data fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const regRes = await regionsService.getAll(true);
        if (regRes?.isSuccess) setRegions(regRes.data);
        
        const facRes = await facilitiesService.getAll(true);
        if (facRes?.isSuccess) setFacilities(facRes.data);
      } catch (error) {
        console.error('Dropdown data fetch error:', error);
      }
    };
    fetchDropdownData();
  }, []);

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filters]);

  const [searchTerm, setSearchTerm] = useState(filters.search || '');

  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters(prev => ({ ...prev, search: searchTerm }));
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => {
      const newFilters = { ...prev, [name]: value };
      // Reset facility if region changes
      if (name === 'regionId') newFilters.facilityId = '';
      return newFilters;
    });
    setPage(1);
  };

  const filteredFacilities = useMemo(() => {
    if (!filters.regionId) return facilities;
    return facilities.filter(f => f.regionId === parseInt(filters.regionId));
  }, [facilities, filters.regionId]);

  const generalRevenueTotal = useMemo(
    () => generalRevenueRows.reduce((sum, row) => sum + Number(row.revenue || 0), 0),
    [generalRevenueRows]
  );

  const handleExportExcel = async () => {
    setIsExportModalOpen(true);
    // Sync modal filters with current dashboard filters
    setExportFilters({
      ...filters,
      status: filters.status || 'success'
    });
  };

  const confirmExportExcel = async () => {
    try {
      setLoading(true);
      const apiExportFilters = { ...exportFilters };
      const res = await paymentDashboardService.downloadTransactionsExcel(apiExportFilters);
      // res is a blob
      const url = window.URL.createObjectURL(new Blob([res]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `odeme_raporu_${new Date().getTime()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setIsExportModalOpen(false);
    } catch (error) {
      console.error('Excel download error:', error);
      alert('Excel dosyası indirilirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const fetchGeneralRevenue = async (nextFilters = generalRevenueFilters) => {
    setGeneralRevenueLoading(true);
    try {
      const res = await paymentDashboardService.getPaymentDashboardStats({
        startDate: nextFilters.startDate,
        endDate: nextFilters.endDate,
        status: 'success'
      });

      if (res?.isSuccess) {
        const rows = [...(res.data?.revenueBreakdown?.facilityRevenues || [])]
          .map(item => ({
            facilityName: item.facilityName || 'Tesis Bilgisi Yok',
            revenue: Number(item.revenue || 0)
          }))
          .filter(item => item.revenue > 0)
          .sort((a, b) => b.revenue - a.revenue);
        setGeneralRevenueRows(rows);
      } else {
        setGeneralRevenueRows([]);
      }
    } catch (error) {
      console.error('General revenue fetch error:', error);
      alert('Genel gelir verileri alinirken bir hata olustu.');
    } finally {
      setGeneralRevenueLoading(false);
    }
  };

  const openGeneralRevenueModal = () => {
    const nextFilters = {
      startDate: filters.startDate || getMonthStartString(),
      endDate: filters.endDate || getTodayString()
    };
    setGeneralRevenueFilters(nextFilters);
    setIsGeneralRevenueModalOpen(true);
    fetchGeneralRevenue(nextFilters);
  };

  const handleGeneralRevenueDateChange = (name, value) => {
    const nextFilters = { ...generalRevenueFilters, [name]: value };
    setGeneralRevenueFilters(nextFilters);
    fetchGeneralRevenue(nextFilters);
  };

  const escapeExcelCell = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

  const downloadGeneralRevenueExcel = () => {
    const rowsHtml = generalRevenueRows.map(row => `
      <tr>
        <td>${escapeExcelCell(row.facilityName)}</td>
        <td style="mso-number-format:'\\#\\,\\#\\#0\\.00';">${Number(row.revenue || 0).toFixed(2)}</td>
      </tr>
    `).join('');

    const html = `
      <html>
        <head><meta charset="UTF-8" /></head>
        <body>
          <table>
            <tr><th>Tesis</th><th>Gelir (TL)</th></tr>
            ${rowsHtml}
          </table>
        </body>
      </html>
    `;

    const blob = new Blob([html], { type: 'application/vnd.ms-excel;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `genel_gelir_${generalRevenueFilters.startDate}_${generalRevenueFilters.endDate}.xls`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleChartClick = (event, elements, chartType) => {
    if (!elements.length) return;
    const { index } = elements[0];
    
    if (chartType === 'pie') {
      const labels = ['Spor Seansları', 'Kart Yükleme', 'Kart & Eğitim Başvuru', 'Kulüp', 'Tenis/Eğitim', 'Diğer'];
      const keys = ['appointmentPayments', 'cardTopUps', 'cardApplications', 'clubPayments', 'education', 'other'];
      const label = labels[index];
      const val = index === 4 
        ? (stats.revenueBreakdown.tennisAcademy || 0) + (stats.revenueBreakdown.publicEducation || 0)
        : stats.revenueBreakdown[keys[index]];

      // Build detail items based on category
      let detailItems = [{ label: 'Kategori Toplamı', value: val }];
      
      if (stats.revenueBreakdown.facilityRevenues) {
        const categoryMap = {
          'Spor Seansları': 'Sports',
          'Kart Yükleme': 'Card',
          'Kart & Eğitim Başvuru': 'Card',
          'Kulüp': 'Club',
          'Tenis/Eğitim': ['Tennis', 'PublicEdu'],
        };
        
        const targetCategory = categoryMap[label];
        
        if (targetCategory) {
          const categoryItems = [];
          stats.revenueBreakdown.facilityRevenues.forEach(f => {
            const matchingSubItems = f.subItems?.filter(s => {
              if (Array.isArray(targetCategory)) return targetCategory.includes(s.category);
              return s.category === targetCategory;
            }) || [];
            matchingSubItems.forEach(sub => {
              categoryItems.push({ label: sub.name, value: sub.revenue });
            });
          });
          
          if (categoryItems.length > 0) {
            // Merge items with same name
            const merged = {};
            categoryItems.forEach(item => {
              if (merged[item.label]) merged[item.label] += item.value;
              else merged[item.label] = item.value;
            });
            detailItems = Object.entries(merged)
              .map(([label, value]) => ({ label, value }))
              .sort((a, b) => b.value - a.value);
          }
        }
      }

      setModalData({
        title: `${label} Detayları`,
        value: val,
        items: detailItems
      });
    } else if (chartType === 'bar') {
      const sorted = [...stats.revenueBreakdown.facilityRevenues]
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 8);
      const facility = sorted[index];
      setModalData({
        title: `${facility.facilityName} Detayları`,
        value: facility.revenue,
        items: facility.subItems && facility.subItems.length > 0 
          ? facility.subItems.map(s => ({ label: s.name, value: s.revenue }))
          : [{ label: 'Toplam Tesis Geliri', value: facility.revenue }]
      });
    }
    setIsModalOpen(true);
  };



  // Memoized Chart Data - Category Breakdown
  const pieData = useMemo(() => {
    if (!stats?.revenueBreakdown) return null;
    const b = stats.revenueBreakdown;
    return {
      labels: ['Spor Seansları', 'Kart Yükleme', 'Kart & Eğitim Başvuru', 'Kulüp', 'Tenis/Eğitim', 'Diğer'],
      datasets: [{
        data: [
          b.appointmentPayments,
          b.cardTopUps,
          b.cardApplications,
          b.clubPayments,
          (b.tennisAcademy || 0) + (b.publicEducation || 0),
          b.other || 0
        ],
        backgroundColor: [
          '#3b82f6', '#10b981', '#f59e0b', '#6366f1', '#8b5cf6', '#94a3b8'
        ],
        borderWidth: 0,
        hoverOffset: 10
      }]
    };
  }, [stats]);

  // Memoized Chart Data - Top Facilities (Bar)
  const barData = useMemo(() => {
    if (!stats?.revenueBreakdown?.facilityRevenues) return null;
    // Get top 8 or all
    const sorted = [...stats.revenueBreakdown.facilityRevenues]
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 8);

    return {
      labels: sorted.map(f => f.facilityName.length > 20 ? f.facilityName.substring(0, 17) + '...' : f.facilityName),
      datasets: [{
        label: 'Revenue (₺)',
        data: sorted.map(f => f.revenue),
        backgroundColor: 'rgba(59, 130, 246, 0.8)',
        borderRadius: 8,
        hoverBackgroundColor: '#3b82f6'
      }]
    };
  }, [stats]);

  const pieOptions = useMemo(() => ({
    maintainAspectRatio: false, 
    plugins: { legend: { position: 'bottom' } },
    onClick: (e, el) => handleChartClick(e, el, 'pie')
  }), [stats]);

  const barOptions = useMemo(() => ({
    maintainAspectRatio: false, 
    plugins: { legend: { display: false } }, 
    scales: { y: { beginAtZero: true } },
    onClick: (e, el) => handleChartClick(e, el, 'bar')
  }), [stats]);

  if (loading && !stats) {
    return (
      <div className="dashboard-loading-overlay">
        <div className="spinner"></div>
        <p>Veriler Analiz Ediliyor...</p>
      </div>
    );
  }

  return (
    <div className="payment-dashboard-v2">
      <div className="dashboard-header-container">
        <div className="header-text">
          <h1>Finansal Özet & Dashboard</h1>
          <p>Tüm ödeme kanallarından gelen verilerin canlı analizi</p>
        </div>
        
        <div className="header-controls">
          <div className="filter-pills">
            <div className="pill select-pill">
              <FaFilter />
              <select name="type" value={filters.type} onChange={handleFilterChange}>
                <option value="">Tüm Kategoriler</option>
                <option value="sports">Spor Seansları</option>
                <option value="cardTopUp">Kart Yükleme</option>
                <option value="cardApp">Kart Başvurusu</option>
                <option value="cardMembership">Paket Satın Alma</option>
                <option value="club">Kulüp Ödemeleri</option>
                <option value="tennis">Tenis Akademisi</option>
                <option value="publicEdu">Halk Eğitim</option>
              </select>
            </div>
            <div className="pill select-pill">
              <FaFilter />
              <select name="regionId" value={filters.regionId} onChange={handleFilterChange}>
                <option value="">Tüm Bölgeler</option>
                {regions.map(r => (
                  <option key={r.regionId} value={r.regionId}>{r.regionName}</option>
                ))}
              </select>
            </div>
            <div className="pill select-pill">
              <FaFilter />
              <select name="facilityId" value={filters.facilityId} onChange={handleFilterChange}>
                <option value="">Tüm Sahalar</option>
                {filteredFacilities.map(f => (
                  <option key={f.facilityId} value={f.facilityId}>{f.facilityName}</option>
                ))}
              </select>
            </div>
            <div className="pill date-pill">
              <FaCalendarAlt />
              <input type="date" name="startDate" value={filters.startDate} onChange={handleFilterChange} />
              <span>-</span>
              <input type="date" name="endDate" value={filters.endDate} onChange={handleFilterChange} />
            </div>
            <button className="refresh-btn" onClick={fetchDashboardData} title="Yenile">
              <FaSyncAlt className={loading ? 'spinning' : ''} />
            </button>
          </div>
        </div>
      </div>

      <div className="kpi-grid">
        <div 
          className="kpi-glass-card clickable"
          onClick={() => {
            if (!stats?.revenueBreakdown) return;
            const b = stats.revenueBreakdown;
            const tA = b.tennisAcademy || 0;
            const pE = b.publicEducation || 0;
            setModalData({
              title: 'Toplam Gelir Kırılımı',
              value: stats.totalRevenue,
              items: [
                { label: 'Spor Seansları', value: b.appointmentPayments || 0 },
                { label: 'Kart Bakiye Yükleme', value: b.cardTopUps || 0 },
                { label: 'Kart & Eğitim Başvuruları', value: b.cardApplications || 0 },
                { label: 'Kulüp Ödemeleri', value: b.clubPayments || 0 },
                { label: 'Tenis ve Halk Eğitim', value: tA + pE },
                { label: 'Diğer Gelirler', value: b.other || 0 },
              ]
            });
            setIsModalOpen(true);
          }}
          title="Gelir detaylarını görmek için tıklayın"
        >
          <div className="kpi-header">
            <div className="icon-box blue"><FaMoneyBillWave /></div>
            <span className="label">Toplam Gelir</span>
          </div>
          <div className="kpi-body">
            <h3>{(stats?.totalRevenue || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</h3>
            <span className="sub">Kategori bazlı detay için tıklayın</span>
          </div>
        </div>
        
        <div className="kpi-glass-card">
          <div className="kpi-header">
            <div className="icon-box green"><FaCheckCircle /></div>
            <span className="label">Bugün</span>
          </div>
          <div className="kpi-body">
            <h3>{(stats?.todayRevenue || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</h3>
            <span className="sub">Günlük performans</span>
          </div>
        </div>

        <div className="kpi-glass-card">
          <div className="kpi-header">
            <div className="icon-box purple"><FaChartPie /></div>
            <span className="label">İşlem Hacmi</span>
          </div>
          <div className="kpi-body">
            <h3>{stats?.successfulTransactions || 0}</h3>
            <span className="sub">Başarılı işlem sayısı</span>
          </div>
        </div>

        <div className="kpi-glass-card">
          <div className="kpi-header">
            <div className="icon-box orange"><FaCreditCard /></div>
            <span className="label">Başarı Oranı</span>
          </div>
          <div className="kpi-body">
            <h3>{stats?.totalTransactions > 0 ? ((stats.successfulTransactions / stats.totalTransactions) * 100).toFixed(1) : 0}%</h3>
            <span className="sub">{stats?.totalTransactions || 0} toplam deneme</span>
          </div>
        </div>
      </div>

      <div className="charts-container">
        <div className="chart-box main-pie">
          <div className="chart-header">
            <h3>Gelir Kırılımı</h3>
            <p>Kanal bazlı dağılım (Detay için tıkla)</p>
          </div>
          <div className="chart-content">
            {pieData && <Pie 
              data={pieData} 
              options={pieOptions}
            />}
          </div>
        </div>

        <div className="chart-box facility-bar">
          <div className="chart-header">
            <h3>Tesis Performansı</h3>
            <p>En çok gelir sağlayan tesisler (Detay için tıkla)</p>
          </div>
          <div className="chart-content">
            {barData && <Bar 
              data={barData} 
              options={barOptions}
            />}
          </div>
        </div>
      </div>

      {/* Transaction Table Section */}
      <div className="table-section shadow-sm">
        <div className="table-header d-flex justify-content-between align-items-center flex-wrap gap-3">
          <h3 className="mb-0">Son İşlemler</h3>
          <div className="search-box-container" style={{ minWidth: '300px' }}>
            <div className="input-group">
              <span className="input-group-text bg-white border-end-0">
                <FaSearch className="text-muted" />
              </span>
              <input
                type="text"
                className="form-control border-start-0 ps-0"
                placeholder="İsim, TC veya İşlem ID ile ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button 
                  className="btn btn-outline-secondary border-start-0" 
                  type="button"
                  onClick={() => setSearchTerm('')}
                >
                  <FaTimes />
                </button>
              )}
            </div>
          </div>
          <div className="table-actions">
            <div className="status-filters">
               <select name="status" value={filters.status} onChange={handleFilterChange}>
                  <option value="">Tüm Durumlar</option>
                  <option value="success">Başarılı</option>
                  <option value="failed">Hatalı</option>
               </select>
            </div>
            <button className="general-revenue-btn" onClick={openGeneralRevenueModal}><FaTable /> Genel Gelir</button>
            <button className="export-btn" onClick={handleExportExcel}><FaDownload /> Excel</button>
          </div>
        </div>

        <div className="table-responsive">
          <table className="modern-table">
            <thead>
              <tr>
                <th>İşlem ID</th>
                <th>Tarih</th>
                <th>Kategori</th>
                <th>Saha</th>
                <th>Müşteri / Detay</th>
                <th>Kart</th>
                <th>Durum</th>
                <th className="text-right">Tutar</th>
              </tr>
            </thead>
            <tbody>
              {transactions.length === 0 ? (
                <tr><td colSpan="8" className="empty-state">Kayıt Bulunamadı</td></tr>
              ) : (
                transactions.map(tx => (
                  <tr key={tx.id}>
                    <td className="tx-id">#{tx.transactionId.substring(0, 8)}</td>
                    <td className="time-col">
                      <span className="date">{new Date(tx.createdAt).toLocaleDateString('tr-TR')}</span>
                      <span className="time">{new Date(tx.createdAt).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</span>
                    </td>
                    <td>
                      <span className={`type-tag ${
                        tx.paymentType?.includes('Bakiye') ? 'topup' : 
                        tx.paymentType?.includes('Spor') ? 'sports' : 
                        tx.paymentType?.includes('Kart Başvuru') ? 'card-app' :
                        tx.paymentType?.includes('Paket') ? 'membership' :
                        tx.paymentType?.includes('Kadın') ? 'women' :
                        tx.paymentType?.includes('Kulüp') ? 'club' :
                        tx.paymentType?.includes('Tenis') ? 'tennis' :
                        'other'
                      }`}>
                        {tx.paymentType}
                      </span>
                    </td>
                    <td className="facility-col">{tx.facilityName || '-'}</td>
                    <td className="user-info-col">{tx.userInfo || 'Anonim Kullanıcı'}</td>
                    <td className="card-col">{tx.cardMask || '**** ****'}</td>
                    <td>
                      <span className={`status-pill ${tx.status}`}>
                        {tx.status === 'success' || tx.status === 'approved' ? 'Başarılı' : 'Başarısız'}
                      </span>
                    </td>
                    <td className="text-right amount-col">
                      {tx.amount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="pagination-v2">
            <button className="page-nav" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
              <FaArrowLeft />
            </button>
            <div className="page-info">
              <span>Sayfa</span>
              <strong>{page} / {totalPages}</strong>
            </div>
            <button className="page-nav next" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
              <FaArrowLeft style={{ transform: 'rotate(180deg)' }} />
            </button>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {isModalOpen && modalData && (
        <div className="dashboard-modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="dashboard-modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{modalData.title}</h3>
              <button className="close-modal" onClick={() => setIsModalOpen(false)}><FaTimesCircle /></button>
            </div>
            <div className="modal-body">
              <div className="total-value">
                <span>Toplam</span>
                <h2>{modalData.value.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</h2>
              </div>
              <div className="modal-items-list">
                {modalData.items.map((item, idx) => (
                  <div key={idx} className="modal-item">
                    <span className="item-label">{item.label}</span>
                    <span className="item-value">{item.value.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* General Revenue Modal */}
      {isGeneralRevenueModalOpen && (
        <div className="dashboard-modal-overlay" onClick={() => setIsGeneralRevenueModalOpen(false)}>
          <div className="dashboard-modal-content general-revenue-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Genel Gelir</h3>
              <button className="close-modal" onClick={() => setIsGeneralRevenueModalOpen(false)}><FaTimesCircle /></button>
            </div>
            <div className="modal-body">
              <div className="general-revenue-toolbar">
                <div className="date-input">
                  <span>Baslangic</span>
                  <input
                    type="date"
                    value={generalRevenueFilters.startDate}
                    onChange={e => handleGeneralRevenueDateChange('startDate', e.target.value)}
                  />
                </div>
                <div className="date-input">
                  <span>Bitis</span>
                  <input
                    type="date"
                    value={generalRevenueFilters.endDate}
                    onChange={e => handleGeneralRevenueDateChange('endDate', e.target.value)}
                  />
                </div>
                <button
                  className="confirm-export-btn compact"
                  onClick={downloadGeneralRevenueExcel}
                  disabled={generalRevenueLoading || generalRevenueRows.length === 0}
                >
                  <FaDownload /> Excel
                </button>
              </div>

              <div className="general-revenue-summary">
                <span>Toplam Gelir</span>
                <strong>{generalRevenueTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</strong>
              </div>

              <div className="general-revenue-table-wrap">
                <table className="general-revenue-table">
                  <thead>
                    <tr>
                      <th>Tesis</th>
                      <th>Gelir</th>
                    </tr>
                  </thead>
                  <tbody>
                    {generalRevenueLoading ? (
                      <tr><td colSpan="2" className="empty-state">Hazirlaniyor...</td></tr>
                    ) : generalRevenueRows.length === 0 ? (
                      <tr><td colSpan="2" className="empty-state">Bu tarih araliginda gelir bulunamadi.</td></tr>
                    ) : (
                      generalRevenueRows.map((row, idx) => (
                        <tr key={`${row.facilityName}-${idx}`}>
                          <td>{row.facilityName}</td>
                          <td>{row.revenue.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Export Selection Modal */}
      {isExportModalOpen && (
        <div className="dashboard-modal-overlay" onClick={() => setIsExportModalOpen(false)}>
          <div className="dashboard-modal-content export-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Excel Raporu Oluştur</h3>
              <button className="close-modal" onClick={() => setIsExportModalOpen(false)}><FaTimesCircle /></button>
            </div>
            <div className="modal-body">
              <div className="export-section">
                <label>Zaman Aralığı Seçin</label>
                <div className="date-presets">
                  <button onClick={() => setExportPreset('today')}>Bugün</button>
                  <button onClick={() => setExportPreset('yesterday')}>Dün</button>
                  <button onClick={() => setExportPreset('week')}>Bu Hafta</button>
                  <button onClick={() => setExportPreset('month')}>Bu Ay</button>
                </div>
                <div className="custom-dates">
                   <div className="date-input">
                     <span>Başlangıç</span>
                     <input type="date" value={exportFilters.startDate} 
                       onChange={e => setExportFilters(prev => ({ ...prev, startDate: e.target.value }))} />
                   </div>
                   <div className="date-input">
                     <span>Bitiş</span>
                     <input type="date" value={exportFilters.endDate} 
                       onChange={e => setExportFilters(prev => ({ ...prev, endDate: e.target.value }))} />
                   </div>
                </div>
              </div>

              <div className="export-section">
                <label>Filtreler</label>
                <div className="modal-filters-grid">
                  <div className="filter-item">
                    <span>Kategori</span>
                    <select value={exportFilters.type} onChange={e => setExportFilters(prev => ({ ...prev, type: e.target.value }))}>
                      <option value="">Tüm Kategoriler</option>
                      <option value="sports">Spor Seansları</option>
                      <option value="cardTopUp">Kart Yükleme</option>
                      <option value="cardApp">Kart Başvurusu</option>
                      <option value="cardMembership">Paket Satın Alma</option>
                      <option value="club">Kulüp Ödemeleri</option>
                      <option value="tennis">Tenis Akademisi</option>
                    </select>
                  </div>
                  <div className="filter-item">
                    <span>Durum</span>
                    <select value={exportFilters.status} onChange={e => setExportFilters(prev => ({ ...prev, status: e.target.value }))}>
                      <option value="">Tüm Durumlar</option>
                      <option value="success">Başarılı</option>
                      <option value="failed">Hatalı</option>
                    </select>
                  </div>
                  <div className="filter-item">
                    <span>Bölge</span>
                    <select value={exportFilters.regionId} onChange={e => {
                      const id = e.target.value;
                      setExportFilters(prev => ({ ...prev, regionId: id, facilityId: '' }));
                    }}>
                      <option value="">Tüm Bölgeler</option>
                      {regions.map(r => <option key={r.regionId} value={r.regionId}>{r.regionName}</option>)}
                    </select>
                  </div>
                  <div className="filter-item">
                    <span>Saha</span>
                    <select value={exportFilters.facilityId} onChange={e => setExportFilters(prev => ({ ...prev, facilityId: e.target.value }))}>
                      <option value="">Tüm Sahalar</option>
                      {facilities
                        .filter(f => !exportFilters.regionId || f.regionId === parseInt(exportFilters.regionId))
                        .map(f => <option key={f.facilityId} value={f.facilityId}>{f.facilityName}</option>)
                      }
                    </select>
                  </div>
                </div>
              </div>

              <button className="confirm-export-btn" onClick={confirmExportExcel} disabled={loading}>
                {loading ? 'Hazırlanıyor...' : <><FaDownload /> Excel Dosyasını İndir</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentDashboard;
